import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  TextField, 
  Button, 
  Typography,
  FormControl,
  FormHelperText,
  CircularProgress,
  Divider
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { validateSQL } from 'helpers/sqlValidation';

// Helper function to parse SQL IN clause and extract values
const parseExistingFilter = (filterValue, columnName) => {
  if (!filterValue) return [];
  
  // Handle different SQL patterns
  const inClausePattern = new RegExp(`${columnName}\\s+IN\\s+\\((.+?)\\)`);
  const nullPattern = new RegExp(`${columnName}\\s+IS\\s+NULL`);
  
  let selectedValues = [];
  
  // Check for combined IN and NULL clause
  if (filterValue.includes(' OR ')) {
    const parts = filterValue.split(' OR ');
    // Extract values from IN clause
    const inMatch = parts[0].match(inClausePattern);
    if (inMatch) {
      selectedValues = inMatch[1].split(',')
        .map(v => v.trim().replace(/^'|'$/g, '')); // Remove quotes
    }
    // Add NULL if it's in the clause
    if (nullPattern.test(parts[1])) {
      selectedValues.push('NULL');
    }
  } else if (inClausePattern.test(filterValue)) {
    // Simple IN clause
    const match = filterValue.match(inClausePattern);
    selectedValues = match[1].split(',')
      .map(v => v.trim().replace(/^'|'$/g, '')); // Remove quotes
  } else if (nullPattern.test(filterValue)) {
    // Simple IS NULL clause
    selectedValues = ['NULL'];
  }
  
  return selectedValues;
};

// Create a cache for distinct values
const distinctValuesCache = new Map();
const getCacheKey = (projectId, datasetId, tableId, columnName) => 
  `${projectId}:${datasetId}:${tableId}:${columnName}`;

const SelectValuesFilter = ({ 
  value,
  onChange, 
  error,
  onValidationErrorChange,
  projectId,
  datasetId,
  tableId,
  columnName 
}) => {
  const [distinctValues, setDistinctValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState(() => {
    return parseExistingFilter(value, columnName);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchSelected, setSearchSelected] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Use refs to prevent infinite loops
  const lastSqlClause = useRef('');
  const isInitialMount = useRef(true);

  const fetchDistinctValues = useCallback(async (isRefresh = false) => {
    try {
      setIsLoading(true);
      if (isRefresh) {
        setIsRefreshing(true);
      }
      setFetchError(null);
      
      // Only check cache if not refreshing
      const cacheKey = getCacheKey(projectId, datasetId, tableId, columnName);
      if (!isRefresh && distinctValuesCache.has(cacheKey)) {
        setDistinctValues(distinctValuesCache.get(cacheKey));
        setIsLoading(false);
        return;
      }
      
      const queryParams = new URLSearchParams();
      queryParams.set("project_id", projectId);
      queryParams.set("dataset_id", datasetId);
      queryParams.set("table_id", tableId);
      queryParams.set("column_name", columnName);

      const response = await fetch(`/bigquery/get-distinct-values/?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      distinctValuesCache.set(cacheKey, data);
      setDistinctValues(data);
    } catch (error) {
      console.error('Error fetching distinct values:', error);
      setFetchError('Failed to load values');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [projectId, datasetId, tableId, columnName]);

  // Initial load
  useEffect(() => {
    if (projectId && datasetId && tableId && columnName) {
      fetchDistinctValues(false); // false = not triggered by user clicking refresh
    }
  }, [fetchDistinctValues]);

  // Update the component when the parent value changes
  useEffect(() => {
    if (!isInitialMount.current && value !== lastSqlClause.current) {
      const parsedValues = parseExistingFilter(value, columnName);
      setSelectedValues(parsedValues);
    }
    isInitialMount.current = false;
  }, [value, columnName]);

  // Update refresh function to use the callback
  const refreshDistinctValues = useCallback(() => {
    const cacheKey = getCacheKey(projectId, datasetId, tableId, columnName);
    distinctValuesCache.delete(cacheKey);
    if (projectId && datasetId && tableId && columnName) {
      fetchDistinctValues(true); // true = triggered by user clicking refresh
    }
  }, [projectId, datasetId, tableId, columnName, fetchDistinctValues]);

  // Helper function to safely convert value to string for comparison
  const safeString = (val) => {
    if (val === null) return 'NULL';
    if (val === undefined) return 'UNDEFINED';
    return String(val);
  };

  // Helper function to format value for SQL
  const formatValueForSQL = (val) => {
    if (val === 'NULL') return 'NULL';  // No quotes for NULL
    if (val === 'UNDEFINED') return 'NULL';  // Treat undefined as NULL
    return `'${val}'`;  // Add quotes for other values
  };

  const filteredAvailableValues = distinctValues.filter(val => 
    !selectedValues.includes(val) && 
    safeString(val).toLowerCase().includes(searchAvailable.toLowerCase())
  );

  const filteredSelectedValues = selectedValues.filter(val =>
    safeString(val).toLowerCase().includes(searchSelected.toLowerCase())
  );

  // Generate SQL clause
  const generateSqlClause = useCallback(() => {
    let sqlClause = '';
    
    if (selectedValues.length > 0) {
      const sqlValues = selectedValues.map(formatValueForSQL).join(', ');
      // Handle NULL values specially in the WHERE clause
      const nullValues = selectedValues.filter(val => val === null || val === 'NULL');
      if (nullValues.length > 0) {
        // If we have both NULL and non-NULL values
        const nonNullValues = selectedValues.filter(val => val !== null && val !== 'NULL');
        if (nonNullValues.length > 0) {
          sqlClause = `(${columnName} IN (${sqlValues}) OR ${columnName} IS NULL)`;
        } else {
          // If we only have NULL values
          sqlClause = `${columnName} IS NULL`;
        }
      } else {
        // If we only have non-NULL values
        sqlClause = `${columnName} IN (${sqlValues})`;
      }
    }
    
    // Validate the generated SQL clause
    if (sqlClause) {
      const validationResult = validateSQL(sqlClause);
      if (!validationResult.isValid) {
        console.warn('Generated invalid SQL:', sqlClause, validationResult.error);
      }
    }
    
    return sqlClause;
  }, [selectedValues, columnName]);

  // Handle validation errors
  useEffect(() => {
    if (onValidationErrorChange) {
      const hasError = selectedValues.length === 0;
      onValidationErrorChange(hasError);
    }
  }, [selectedValues, onValidationErrorChange]);

  // Update the parent component with SQL changes
  useEffect(() => {
    const newSqlClause = generateSqlClause();
    
    // Only trigger onChange if the SQL has actually changed
    if (newSqlClause !== lastSqlClause.current) {
      lastSqlClause.current = newSqlClause;
      onChange(newSqlClause, 'select');
    }
  }, [selectedValues, onChange, generateSqlClause]);

  const handleAddValue = (value) => {
    setSelectedValues(prev => {
      // Prevent duplicates
      if (prev.includes(value)) return prev;
      return [...prev, value];
    });
  };

  const handleRemoveValue = (value) => {
    setSelectedValues(prev => prev.filter(v => v !== value));
  };

  const handleAddFiltered = () => {
    setSelectedValues(prev => {
      // Create a new array with all existing values plus new ones (avoiding duplicates)
      const existingSet = new Set(prev);
      filteredAvailableValues.forEach(val => existingSet.add(val));
      return Array.from(existingSet);
    });
  };

  const handleRemoveAll = () => {
    if (!searchSelected) {
      // If no filter is applied, remove all values
      setSelectedValues([]);
    } else {
      // If filter is applied, only remove the filtered values
      const valuesToKeep = selectedValues.filter(
        val => !safeString(val).toLowerCase().includes(searchSelected.toLowerCase())
      );
      setSelectedValues(valuesToKeep);
    }
  };

  if (isLoading) {
    return (
      <FormControl fullWidth sx={{ mt: 1, minHeight: 40, display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={24} />
      </FormControl>
    );
  }

  if (fetchError) {
    return (
      <FormControl fullWidth sx={{ mt: 1 }} error>
        <FormHelperText>{fetchError}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth sx={{ mt: 1 }} error={error}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Available Values Panel */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              Available Values
            </Typography>
            <Button 
              size="small" 
              onClick={refreshDistinctValues}
              disabled={isRefreshing}
              startIcon={
                isRefreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )
              }
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Values'}
            </Button>
          </Box>
          
          {/* Show loading overlay when refreshing */}
          <Box sx={{ position: 'relative' }}>
            {isRefreshing && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            
            <TextField
              size="small"
              fullWidth
              placeholder="Search available values..."
              value={searchAvailable}
              onChange={(e) => setSearchAvailable(e.target.value)}
              sx={{ mb: 1 }}
              disabled={isRefreshing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button 
            size="small" 
            onClick={handleAddFiltered}
            disabled={filteredAvailableValues.length === 0}
            sx={{ mb: 1 }}
          >
            {searchAvailable ? 'Add Filtered →' : 'Add All →'}
          </Button>
          <List sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper' }}>
            {filteredAvailableValues.map((val) => (
              <ListItem
                key={val}
                dense
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleAddValue(val)}>
                    <ArrowForwardIcon />
                  </IconButton>
                }
              >
                <ListItemText 
                  primary={safeString(val)} 
                  sx={val === null || val === 'NULL' ? { fontStyle: 'italic' } : {}}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Selected Values Panel */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Values
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search selected values..."
            value={searchSelected}
            onChange={(e) => setSearchSelected(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button 
            size="small" 
            onClick={handleRemoveAll}
            disabled={searchSelected ? filteredSelectedValues.length === 0 : selectedValues.length === 0}
            sx={{ mb: 1 }}
          >
            {searchSelected ? '← Remove Filtered' : '← Remove All'}
          </Button>
          <List sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper' }}>
            {filteredSelectedValues.map((val) => (
              <ListItem
                key={val}
                dense
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleRemoveValue(val)}>
                    <ArrowBackIcon />
                  </IconButton>
                }
              >
                <ListItemText 
                  primary={safeString(val)} 
                  sx={val === null || val === 'NULL' ? { fontStyle: 'italic' } : {}}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
      {error && <FormHelperText>Please select at least one value</FormHelperText>}
    </FormControl>
  );
};

export default SelectValuesFilter; 